import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { User, LogOut, Download, Settings, Zap, Mail, CreditCard } from "lucide-react";
import { JobQuotaBadge } from "./JobQuotaBadge";

export const Navbar = ({ token, quota, onQuotaUpdate, onBuyCreditsClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <nav className="glass-nav sticky top-0 z-50 px-6 py-3" data-testid="navbar">
      <div className="max-w-[1800px] mx-auto flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
          <img 
            src="https://customer-assets.emergentagent.com/job_d376b3b4-e712-4a08-ace0-a95651d952ab/artifacts/jkta9457_OneWay.png" 
            alt="OneWay" 
            className="h-[100px]"
            data-testid="navbar-logo"
          />
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-4">
          {/* Job Quota Badge */}
          <JobQuotaBadge 
            token={token} 
            quota={quota} 
            onQuotaUpdate={onQuotaUpdate}
            onBuyClick={onBuyCreditsClick}
          />

          {/* Email Tracking */}
          <Button 
            variant="outline" 
            size="sm"
            className="border-[#A3C3D8] text-[#4A728D] hover:bg-[#A3C3D8]/10"
            onClick={() => navigate('/email-tracking')}
            data-testid="email-tracking-btn"
          >
            <Mail className="h-4 w-4 mr-2" />
            Email Tracking
          </Button>

          {/* Autofill Profile */}
          <Button 
            variant="outline" 
            size="sm"
            className="border-slate-200 text-[#555F67] hover:bg-slate-50"
            onClick={() => navigate('/profile')}
            data-testid="autofill-profile-btn"
          >
            <Zap className="h-4 w-4 mr-2" />
            Autofill Profile
          </Button>

          {/* Extension Download */}
          <Button 
            variant="ghost" 
            size="sm"
            className="text-[#94A3B8] hover:text-[#555F67]"
            onClick={() => window.open('https://chromewebstore.google.com/detail/oneway-job-tracker/mnlccbpppnjgbhoklajdoogagjoknnob?hl=en-GB&utm_source=ext_sidebar', '_blank', 'noopener,noreferrer')}
            data-testid="download-extension-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Extension
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="flex items-center gap-2 px-3 hover:bg-slate-100"
                data-testid="user-menu-trigger"
              >
                <div className="w-8 h-8 rounded-full bg-[#A3C3D8] flex items-center justify-center">
                  <span className="text-sm font-medium text-[#152A38]">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <span className="text-sm font-medium text-[#555F67] hidden sm:block">
                  {user?.name}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2">
                <p className="text-sm font-medium text-[#2D3748]">{user?.name}</p>
                <p className="text-xs text-[#94A3B8]">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="cursor-pointer"
                onClick={() => navigate('/profile')}
              >
                <User className="mr-2 h-4 w-4" />
                Autofill Profile
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="cursor-pointer"
                onClick={() => navigate('/email-tracking')}
              >
                <Mail className="mr-2 h-4 w-4" />
                Email Tracking
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="cursor-pointer"
                onClick={onBuyCreditsClick}
              >
                <CreditCard className="mr-2 h-4 w-4" />
                Buy Job Credits
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="cursor-pointer text-red-600 focus:text-red-600"
                onClick={logout}
                data-testid="logout-btn"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
